import {
  BaseApplicant as Applicant,
  ErrorPage,
  ESessionStorageKeys,
  FinancingConfigDto,
  Loader,
  StepperFormFooter,
  useMerchantFinancingConfig,
} from '@applyonline/shared'
import { scrollToTop } from '@applyonline/shared/utils'
import { EFinancingProgram } from '@if/core/types'
import { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { buildPrequalificationSchema, Prequalification } from '../../types/Schemas/PrequalificationSchema'
import { useSessionStorage } from '../InitialApprobationPage/hooks'
import EmploymentForm from './components/EmploymentForm'
import LoanAmountForm from './components/LoanAmountForm'
import PersonalInformationForm from './components/PersonalInformationForm'
import PersonnalDetailsForm from './components/PersonnalDetailsForm'
import ReviewPrequalification from './components/ReviewPrequalification'
import StepProgressionBar from './components/StepProgressionBar'
import { useMerchantIdFromSubdomain } from './hooks'
enum Step {
  PersonalInformation = 'personal-information',
  LoanAmount = 'loan-amount',
  Contact = 'contact',
  Employment = 'employment',
  Review = 'review',
}

const STEP_LIST = Object.values(Step)

const ApplyWrapper = () => {
  const merchantId = useMerchantIdFromSubdomain()
  const [financingProgramConfig, isFetchingConfig, fetchingConfigError] = useMerchantFinancingConfig(
    merchantId,
    EFinancingProgram.B2c,
  )
  if (fetchingConfigError != null) return <ErrorPage />
  if (isFetchingConfig) return <Loader description="" />
  return <Apply financingProgramConfig={financingProgramConfig!} />
}

interface Props {
  financingProgramConfig: FinancingConfigDto
}

const Apply = ({ financingProgramConfig }: Props) => {
  const { t } = useTranslation()
  const { currentstep: paramCurrentStep } = useParams()
  const currentStep: Step = (paramCurrentStep as Step) || Step.PersonalInformation
  const merchantId = useMerchantIdFromSubdomain()
  const [prequalInfo, setPrequalInfo] = useSessionStorage<Prequalification>(ESessionStorageKeys.PREQUAL_INFO, {
    ...(buildPrequalificationSchema(financingProgramConfig).getDefault() as unknown as Prequalification),
    lastTouchOrigination: null,
    firstTouchOrigination: null,
    merchantId,
  })
  const navigate = useNavigate()

  const currentStepIndex = STEP_LIST.indexOf(currentStep)

  const [disableNext, setDisableNext] = useState(false)

  useLayoutEffect(scrollToTop, [])

  useEffect(() => {
    const savedPrequalInfo = sessionStorage.getItem(ESessionStorageKeys.PREQUAL_INFO)
    if (!savedPrequalInfo) navigate(`/apply/${Step.PersonalInformation}`, { replace: true })
  }, [navigate])

  const gotoStep = useCallback(
    (stepPath: string) => {
      scrollToTop()

      navigate(`/apply/${stepPath}`)
    },
    [navigate],
  )

  const nextStepAfter = useCallback(
    (cCurrentStep: string) => {
      const nextStepIdx = STEP_LIST.indexOf(cCurrentStep.toLowerCase() as Step) + 1
      if (nextStepIdx < STEP_LIST.length) {
        gotoStep(STEP_LIST[nextStepIdx])
      }
    },
    [gotoStep],
  )

  const handlePrequalificationChanged = (data: Partial<Prequalification>) => {
    const update = { ...prequalInfo, ...data }
    setPrequalInfo(update)
  }

  const handleApplicantChanged = (data: Partial<Applicant>) => {
    const update = { ...prequalInfo, applicant: { ...prequalInfo.applicant, ...data } }
    setPrequalInfo(update)
  }

  return (
    <main className={`form-section step-${currentStepIndex + 1}-active`}>
      <div className="strong text-center" style={{ marginBottom: '3rem', fontSize: '0.9em' }}>
        {t('eligibility.preApproved')}
      </div>
      <StepProgressionBar stepIndex={currentStepIndex} />
      {currentStep === Step.PersonalInformation && (
        <PersonalInformationForm
          prequalificationData={prequalInfo.applicant}
          onPrequalificationChanged={handleApplicantChanged}
          nextStepAfter={nextStepAfter}
          currentStep={currentStep}
          merchantId={merchantId}
        />
      )}

      {currentStep === Step.LoanAmount && (
        <LoanAmountForm
          prequalificationData={prequalInfo}
          onPrequalificationChanged={handlePrequalificationChanged}
          nextStepAfter={nextStepAfter}
          currentStep={currentStep}
          financingProgramConfig={financingProgramConfig}
        />
      )}

      {currentStep === Step.Contact && (
        <PersonnalDetailsForm
          onApplicantChanged={handleApplicantChanged}
          nextStepAfter={nextStepAfter}
          currentStep={currentStep}
          applicantData={prequalInfo.applicant}
          setDisableNext={setDisableNext}
        />
      )}

      {currentStep === Step.Employment && (
        <EmploymentForm
          applicantData={prequalInfo.applicant}
          onApplicantChanged={handleApplicantChanged}
          nextStepAfter={nextStepAfter}
          currentStep={currentStep}
        />
      )}

      {currentStep === Step.Review && <ReviewPrequalification gotoStep={gotoStep} prequalification={prequalInfo} />}

      {currentStep !== Step.Review && (
        <StepperFormFooter
          currentStepIndex={currentStepIndex}
          disableBack={currentStepIndex === 0}
          disableNext={disableNext}
          setDisableNext={setDisableNext}
          steps={STEP_LIST}
          baseURL="/apply"
        />
      )}
    </main>
  )
}

export default ApplyWrapper
